import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  Bell, 
  X, 
  Check, 
  CheckCheck, 
  Trash2, 
  Settings, 
  AlertCircle, 
  Clock, 
  User, 
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Circle
} from 'lucide-react';

// Color palette for team tags - consistent colors per team
const TEAM_COLORS = [
  { bg: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-green-100', text: 'text-green-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

// Get consistent color for a team based on team_id
const getTeamColor = (teamId) => {
  if (!teamId) return TEAM_COLORS[0];
  // Simple hash function to get consistent index
  let hash = 0;
  for (let i = 0; i < teamId.length; i++) {
    hash = ((hash << 5) - hash) + teamId.charCodeAt(i);
    hash = hash & hash;
  }
  return TEAM_COLORS[Math.abs(hash) % TEAM_COLORS.length];
};

// Team tag component with consistent coloring
const TeamTag = ({ teamId, name }) => {
  const colors = getTeamColor(teamId);
  // Truncate long names to prevent UI issues
  const displayName = name && name.length > 20 ? name.substring(0, 18) + '…' : name;
  return (
    <span 
      className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${colors.bg} ${colors.text}`}
      title={name}
    >
      {displayName}
    </span>
  );
};

// Priority icons and colors
const PRIORITY_CONFIG = {
  low: { icon: Circle, color: 'text-gray-500', bgColor: 'bg-gray-50' },
  normal: { icon: Circle, color: 'text-blue-500', bgColor: 'bg-blue-50' },
  high: { icon: AlertCircle, color: 'text-orange-500', bgColor: 'bg-orange-50' },
  urgent: { icon: AlertTriangle, color: 'text-red-500', bgColor: 'bg-red-50' }
};

// Notification type icons
const TYPE_ICONS = {
  task_assigned: User,
  task_due_soon: Clock,
  task_due_today: AlertCircle,
  task_overdue: AlertTriangle,
  task_completed: CheckCircle,
  task_status_changed: Circle,
  comment_added: MessageSquare,
  team_member_added: User,
  system_maintenance: Settings
};

const NotificationItem = ({ notification, onMarkRead, onDelete, onNavigate }) => {
  const PriorityIcon = PRIORITY_CONFIG[notification.priority]?.icon || Circle;
  const TypeIcon = TYPE_ICONS[notification.type] || Bell;
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Mark as read
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }
    
    // Navigate to action URL if available
    if (notification.action_url && onNavigate) {
      try {
        // Parse data - it might be a string or already an object
        let parsedData = {};
        if (notification.data) {
          if (typeof notification.data === 'string') {
            parsedData = JSON.parse(notification.data);
          } else {
            parsedData = notification.data;
          }
        }
        
        // Store navigation intent for App.js to handle on next page load (backup)
        const notificationNav = {
          url: notification.action_url,
          type: notification.type,
          data: parsedData,
          timestamp: Date.now()
        };
        
        sessionStorage.setItem('notificationNav', JSON.stringify(notificationNav));
        
        // Navigate using React Router for smooth navigation (no page reload)
        let targetUrl = notification.action_url;
        
        if (notification.action_url.startsWith('/')) {
          // Already relative - use React Router
          onNavigate(targetUrl);
        } else if (notification.action_url.startsWith('http')) {
          // Full URL - extract path and use React Router
          try {
            const url = new URL(notification.action_url);
            targetUrl = url.pathname + url.search + url.hash;
            onNavigate(targetUrl);
          } catch (error) {
            console.error('Failed to parse URL:', error);
          }
        }
      } catch (error) {
        console.error('Error handling notification click:', error);
      }
    }
  };

  return (
    <div 
      className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      } ${notification.action_url ? 'cursor-pointer' : ''}`}
      onClick={handleNotificationClick}
    >
      <div className="flex items-start space-x-3">
        {/* Priority and Type Icons */}
        <div className="flex items-center space-x-1">
          <div className={`p-2 rounded-full ${PRIORITY_CONFIG[notification.priority]?.bgColor}`}>
            <TypeIcon className={`w-4 h-4 ${PRIORITY_CONFIG[notification.priority]?.color}`} />
          </div>
          {!notification.is_read && (
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${
                !notification.is_read ? 'text-gray-900' : 'text-gray-700'
              }`}>
                {notification.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {notification.message}
              </p>
              <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                <span>{formatTimeAgo(notification.created_at)}</span>
                {(notification.company_name || notification.team_name) && (
                  <>
                    <span>•</span>
                    <TeamTag teamId={notification.team_id} name={notification.company_name || notification.team_name} />
                  </>
                )}
                {notification.category && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{notification.category}</span>
                  </>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-1 ml-3">
              {!notification.is_read && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkRead(notification.id);
                  }}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Mark as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(notification.id);
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationCenter = ({ isOpen, onClose, user, onNotificationsChanged }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, task, system
  const [notificationCount, setNotificationCount] = useState({ total: 0, unread: 0 });

  const fetchNotifications = async () => {
    try {
      const unreadOnly = filter === 'unread';
      const category = filter === 'task' ? 'task' : filter === 'system' ? 'system' : null;
      
      const params = new URLSearchParams();
      if (unreadOnly) params.append('unread_only', 'true');
      if (category) params.append('category', category);
      
      const response = await api.get(`/notifications?${params}`);
      
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/notifications/count');
      setNotificationCount(response.data);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const refreshCounts = async () => {
    await fetchNotificationCount();
    if (onNotificationsChanged) {
      onNotificationsChanged();
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
      fetchNotificationCount();
    }
  }, [isOpen, filter, user]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}`, {
        is_read: true
      });
      
      await fetchNotifications();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      
      await fetchNotifications();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (processing) return;
    
    try {
      setProcessing(true);
      await api.put('/notifications/mark-all-read', {});
      
      await fetchNotifications();
      await refreshCounts();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      alert(`Failed to mark notifications as read: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleClearAll = async () => {
    if (processing) return;
    
    if (window.confirm('Are you sure you want to delete all notifications? This cannot be undone.')) {
      try {
        setProcessing(true);
        await api.delete('/notifications/clear-all');
        
        await fetchNotifications();
        await refreshCounts();
      } catch (error) {
        console.error('Failed to clear notifications:', error);
        alert(`Failed to delete notifications: ${error.response?.data?.detail || error.message}`);
      } finally {
        setProcessing(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <p className="text-sm text-gray-600">
                {notificationCount.unread} unread of {notificationCount.total} total
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2 mt-4">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'task', label: 'Tasks' },
              { key: 'system', label: 'System' }
            ].map((filterOption) => (
              <button
                key={filterOption.key}
                onClick={() => setFilter(filterOption.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filter === filterOption.key
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filterOption.label}
              </button>
            ))}
          </div>

          {/* Actions */}
          {notifications.length > 0 && (
            <div className="flex items-center space-x-2 mt-3">
              <button
                onClick={handleMarkAllRead}
                disabled={processing}
                className={`flex items-center space-x-1 px-3 py-1 text-xs rounded-lg transition-colors ${
                  processing 
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-blue-600 hover:bg-blue-50'
                }`}
              >
                <CheckCheck className="w-3 h-3" />
                <span>{processing ? 'Processing...' : 'Mark all read'}</span>
              </button>
              <button
                onClick={handleClearAll}
                disabled={processing}
                className={`flex items-center space-x-1 px-3 py-1 text-xs rounded-lg transition-colors ${
                  processing 
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 className="w-3 h-3" />
                <span>{processing ? 'Processing...' : 'Clear all'}</span>
              </button>
            </div>
          )}
        </div>

        {/* Content - Scrollable area with scroll containment */}
        <div 
          className="flex-1 overflow-y-auto overscroll-contain min-h-0"
          onWheel={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading notifications...</p>
            </div>
          ) : notifications.length > 0 ? (
            <div>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                  onNavigate={(url) => {
                    onClose(); // Close notification panel first
                    navigate(url);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
              </h3>
              <p className="text-gray-600">
                {filter === 'unread' 
                  ? "You're all caught up! No unread notifications."
                  : "You'll see notifications for tasks, comments, and system updates here."
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
